import React, { useEffect } from "react";

import classnames from "classnames";
import { DownMinimalArrow } from "icons";
import { isEmpty } from "ramda";
import { useDrop } from "react-dnd";

import { Typography } from "clearsense-ui/components";
import { Close, UpMinimalArrow } from "clearsense-ui/icons";

const ValueAccordion = ({
  secondRefValue,
  firstRefValues,
  isExpanded,
  onMapping,
  targetValues,
  toggleVisibility,
  selectedValueId,
  disassociateValue,
  style,
}) => {
  const mappedValuesCount = secondRefValue?.values?.length;
  const getNameOfMappedValue = valueId =>
    firstRefValues.find(value => value.id === valueId)?.["name"];

  const [{ isOver, isDropped }, drop] = useDrop(
    () => ({
      accept: "referenceSourceValue",
      drop(item) {
        if (item) {
          onMapping(item.value, secondRefValue);
        }
      },
      collect: monitor => ({
        isOver: monitor.isOver(),
        isDropped: monitor.didDrop(),
      }),
    }),
    [targetValues]
  );

  const onDragEnter = valueId => {
    if (!isExpanded[valueId]) {
      toggleVisibility(valueId);
    }
  };

  const onDragLeave = valueId => {
    if (isExpanded[valueId]) {
      toggleVisibility(valueId);
    }
  };

  useEffect(() => {
    if (isOver) {
      onDragEnter(secondRefValue.id);
    } else if (!isOver && !isDropped) {
      onDragLeave(secondRefValue.id);
    }
  }, [isOver]);

  return (
    <div
      ref={drop}
      onClick={() => toggleVisibility(secondRefValue.id)}
      className={classnames(
        "p-3 rounded-md shadow-sm bg-gray-50 border border-solid border-gray-300 flex flex-col dark:text-gray-100 select-none dark:bg-gray-700 dark:border-black",
        {
          "border-indigo-500": isOver && isExpanded[secondRefValue.id],
        }
      )}
      style={style}
      data-cy="reference-source-droppable-card"
    >
      <div className="flex justify-between">
        <div className="flex flex-col">
          <Typography style="large" type="semibold">
            {secondRefValue.name}
            {mappedValuesCount !== 0 ? `(${mappedValuesCount})` : ""}
          </Typography>
          <Typography style="small" className="text-gray-400">
            {secondRefValue.description}
          </Typography>
        </div>
        <div className="my-auto">
          {isExpanded[secondRefValue.id] ? (
            <UpMinimalArrow size={16} className="text-gray-400" />
          ) : (
            <DownMinimalArrow size={16} className="text-gray-400" />
          )}
        </div>
      </div>
      {isExpanded[secondRefValue.id] && (
        <div className="my-2">
          {!isEmpty(secondRefValue.values) ? (
            <div className="flex gap-4 flex-wrap justify-start h-full overflow-y-auto">
              {secondRefValue.values.map(valueId => (
                <div
                  key={secondRefValue.id}
                  className={classnames(
                    "rounded-md shadow-sm bg-blue-100 border border-solid border-gray-300 flex justify-between p-1 space-x-2 h-8  dark:border-blue-500 dark:bg-blue-500",
                    {
                      "border-indigo-500 text-indigo-500 dark:text-white dark:border-indigo-600 dark:bg-indigo-600":
                        isExpanded[secondRefValue.id] &&
                        valueId === selectedValueId,
                    }
                  )}
                >
                  <Typography style="medium" data-cy="value-label">
                    {getNameOfMappedValue(valueId)}
                  </Typography>
                  <Close
                    size={16}
                    onClick={event => disassociateValue(event, valueId)}
                    className={classnames("m-auto cursor-pointer", {
                      "text-indigo-500 dark:text-white":
                        isExpanded[secondRefValue.id] &&
                        valueId === selectedValueId,
                    })}
                    data-cy="value-unMap-icon"
                  />
                </div>
              ))}
            </div>
          ) : (
            <Typography
              style="medium"
              className="text-gray-400 flex justify-center items-center h-full"
            >
              You can drag and drop or add values from the left hand side panel
              to here.
            </Typography>
          )}
        </div>
      )}
    </div>
  );
};

export default ValueAccordion;
